import searchCity from '../api/searchCity';
import {always, defaultTo, pipe, prop, tap} from 'ramda';
import {first, fromEvent, map, mergeMap, race, Subject} from 'rxjs';
import createCache from '../libs/map';
import {getElementById, triggerChange} from '../dom-utils';

/** @typedef {import('./api/searchCity').City} City */

const noop = always(undefined);
/**
 *
 * @param {City} city
 */
const cityToString = ({region, text: city}) => region ? `${city}, ${region}` : city;

/** @type {Map<string, City[]>} */
const {
    has: hasInCache,
    set: putInCache,
    get: readFromCache,
} = createCache();

/**
 *
 * @param {HTMLInputElement} input
 * @returns
 */
export default async function createCitySearch(input) {
    if (!input) return;

    const hiddenInput = getElementById(input.dataset.controls);

    if (!hiddenInput) return;

    const {default: autocomplete} = await import('autocomplete.js');
    const {default: {CancelToken}} = await import('axios');
    const focusEvents$ = fromEvent(input, 'focus');
    const blurEvents$ = fromEvent(input, 'blur', {once: true});
    const selectedEvents$ = new Subject();
    const callback = defaultTo(noop, prop(input.dataset.jsCallback, window));
    let cancelPreviousRequest = noop;

    const autocompleteInstance = autocomplete(input, {
            debug: DEVELOPMENT,
            cssClasses: {
                noPrefix: true,
                root: 'autocomplete',
                dropdownMenu: 'autocomplete-dropdown',
                suggestions: 'autocomplete-suggestions',
                suggestion: 'autocomplete-suggestion',
                cursor: 'is-selected',
                empty: 'autocomplete-suggestions',
            },
            templates: {
                empty: () => 'По запросу ничего не найдено',
            },
        }, [
            {
                source: (query, cb) => {
                    cancelPreviousRequest();

                    if (hasInCache(query))
                        return cb(readFromCache(query));

                    return searchCity(query, {
                        cancelToken: new CancelToken((cancelFn) => cancelPreviousRequest = cancelFn),
                    }).then(pipe(
                        tap(putInCache(query)),
                        cb
                    )).catch(noop);
                },
                templates: {
                    suggestion: cityToString,
                },
                displayKey: cityToString,
                debounce: 350,
                cache: true, // Doesn't work with custom source providers
            }
        ]
    );

    autocompleteInstance.on('autocomplete:selected', (event, suggestion) =>
        selectedEvents$.next([event, suggestion])
    );

    selectedEvents$.subscribe(([event, suggestion]) => {
        hiddenInput.value = suggestion.id;
        callback(event, suggestion);
        triggerChange(hiddenInput);
    });

    focusEvents$.pipe(
        map(event => [event.target, event.target.value]),
        mergeMap(([input, value]) =>
            race(
                blurEvents$.pipe(map(() => [input, value])),
                selectedEvents$.pipe(
                    first(),
                    map(([event, suggestion]) => suggestion),
                    map(cityToString),
                    map(value => [input, value]),
                )
            )
        )
    ).subscribe(([input, value]) => input.value = value);

    return autocompleteInstance;
}
